import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Container, Card, Grid, Dropdown, Checkbox, Select, Button, Icon} from 'semantic-ui-react';
import {hasAccessPermission} from '../../../common/permissions/validations';
import {scopes, resources} from '../../../common/permissions/dictionary';
import {paths} from '../../../common/dictionaries/urls';
import {Actions} from '../../../common/actions';
import {getDropdownOptions} from '../../../common/util/dropdown';
import WtsEmployeeModal from './employeeModal';
import CommercialTable from '../../../common/components/commercialTable/commercialTable';
import './styles.scss';
import WtsSkillsTechDetailsModal from './skillsTechDetailsModal';
import {DefaultColumnFilter} from '../../../manageOrders/components/internalCommons/TableFilters';
import {renderTableLoader} from '../../../common/components/tableLoader';
import moment from 'moment';

const {
  navigateTo,
  getWtsAreas,
  changeWtsAreaSelected,
  updateWtsEmployeeStatus,
  getWtsEmployeesGestor,
  getWtsDealer,
} = Actions;

const SortIcon = ({direction}) => {
  if (!direction) {
    return (
      <span style={{'marginLeft': '5px', 'opacity': '0.4', 'fontSize': '1.2em'}}>⇅</span>
    );
  }
  return (
    <span style={{'marginLeft': '5px', 'fontSize': '1.2em'}}>
      {direction === 'asc' ? '↑' : '↓'}
    </span>
  );
};

// eslint-disable-next-line max-statements
const WtsEmployees = () => {
  const permissions = useSelector((state) => state.session.permissions);
  const {areas, areaSelected} = useSelector((state) => state.wtsAreas);
  const wtsEmployees = useSelector((state) => state.wtsEmployees.employeesGestor || []);
  const loading = useSelector((state) => state.wtsEmployees.loading);
  const [filteredCount, setFilteredCount] = useState(0);
  const [sortConfig, setSortConfig] = useState({'key': null, 'direction': null});
  const [tableKey, setTableKey] = useState(0);

  const dispatch = useDispatch();

  useEffect(() => {
    if (!hasAccessPermission(permissions, resources.WtsEmpleados, scopes.See) &&
     !hasAccessPermission(permissions, resources.WtsEmpleados, scopes.Edit)) {
      dispatch(navigateTo(paths.AccessDenied));
    }
    dispatch(getWtsAreas());
    dispatch(getWtsEmployeesGestor(1, '', ''));
    dispatch(changeWtsAreaSelected(1));
  }, [dispatch, permissions]);

  useEffect(() => {
    if (areaSelected?.id) {
      dispatch(getWtsDealer(areaSelected.id));
    }
  }, [areaSelected, dispatch]);

  const onChangeWtsAreaSelected = (e, {value}) => {
    e.preventDefault();
    const selected = areas.find((x) => x.id === value);
    dispatch(changeWtsAreaSelected(selected));
    dispatch(getWtsEmployeesGestor(value, '', ''));
  };

  const updateStatus = (e, checkedValue, item) => {
    e.preventDefault();
    dispatch(updateWtsEmployeeStatus(areaSelected.id,
      {'data': {...item, 'status': checkedValue ? 'ACTIVO' : 'INACTIVO'}}));
  };

  const nextEmployeeId = React.useMemo(() => {
    if (wtsEmployees.length === 0) {
      return 1;
    }
    const ids = wtsEmployees.map((emp) => parseInt(emp.id, 10)).filter((id) => !isNaN(id));
    return ids.length > 0 ? Math.max(...ids) + 1 : 1;
  }, [wtsEmployees]);

  const handleSort = (accessor) => {
    setSortConfig((prev) => {
      if (prev.key === accessor) {
        if (prev.direction === 'asc') {
          return {'key': accessor, 'direction': 'desc'};
        }
        if (prev.direction === 'desc') {
          return {'key': null, 'direction': null};
        }
      }
      return {'key': accessor, 'direction': 'asc'};
    });
  };

  const sortedEmployees = React.useMemo(() => {
    const mappedEmployees = wtsEmployees.map((emp) => {
      const allCerts = [...(emp.certificatesTmc || []), ...(emp.certificatesTde || [])]
        .sort((a, b) => {
          const dateA = a.date ? moment(a.date).valueOf() : 0;
          const dateB = b.date ? moment(b.date).valueOf() : 0;
          return dateB - dateA;
        })
        .map((c) => c.certificate || '')
        .filter(Boolean);

      return {
        ...emp,
        'allCertificates': allCerts,
      };
    });

    if (!sortConfig.key || !sortConfig.direction) {
      return mappedEmployees;
    }
    // eslint-disable-next-line max-statements
    return [...mappedEmployees].sort((a, b) => {
      let valA = a[sortConfig.key];
      let valB = b[sortConfig.key];

      if (Array.isArray(valA)) {
        valA = valA[0] || '';
      }
      if (Array.isArray(valB)) {
        valB = valB[0] || '';
      }

      const dateFields = ['startDate'];
      if (dateFields.includes(sortConfig.key)) {
        valA = valA ? moment(valA).valueOf() : 0;
        valB = valB ? moment(valB).valueOf() : 0;
        return sortConfig.direction === 'asc' ? valA - valB : valB - valA;
      }

      const numA = parseFloat(valA);
      const numB = parseFloat(valB);
      if (!isNaN(numA) && !isNaN(numB)) {
        return sortConfig.direction === 'asc' ? numA - numB : numB - numA;
      }

      const strA = (valA || '').toString().toLowerCase();
      const strB = (valB || '').toString().toLowerCase();
      if (strA < strB) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (strA > strB) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [wtsEmployees, sortConfig]);

  const makeSortableHeader = (label, accessor) => () => (
    <span
      className="header-title"
      onClick={() => handleSort(accessor)}
      style={{'cursor': 'pointer', 'userSelect': 'none', 'whiteSpace': 'nowrap'}}
    >
      {label}
      <SortIcon direction={sortConfig.key === accessor ? sortConfig.direction : null} />
    </span>
  );

  const ExcelColumnFilter = ({
    'column': {filterValue, setFilter, preFilteredRows, id},
  }) => {
    const options = React.useMemo(() => {
      const opts = new Set();
      const colId = Array.isArray(id) ? id[0] : id;
      preFilteredRows.forEach((row) => {
        const val = row.values[colId];
        if (val !== null && typeof val !== 'undefined' && val !== '') {
          if (Array.isArray(val)) {
            val.forEach((v) => {
              if (v !== null && typeof v !== 'undefined' && v !== '') {
                opts.add(v);
              }
            });
          } else {
            opts.add(val);
          }
        }
      });
      return [...opts.values()].sort();
    }, [id, preFilteredRows]);

    const dropdownOptions = [
      {'key': 'all', 'value': '', 'text': 'TODOS'},
      ...options.map((opt, i) => ({'key': i, 'value': opt, 'text': opt})),
    ];

    return (
      <Select
        compact
        search
        placeholder="TODOS"
        value={filterValue || ''}
        options={dropdownOptions}
        style={{'minWidth': '120px', 'width': '100%'}}
        onChange={(e, {value}) => setFilter(value || '')}
      />
    );
  };

  const exactTextFilter = (rows, id, filterValue) => {
    if (filterValue === '' || !filterValue) {
      return rows;
    }
    const colId = Array.isArray(id) ? id[0] : id;
    return rows.filter((row) => {
      const val = row.values[colId];
      if (Array.isArray(val)) {
        return val.includes(filterValue);
      }
      return val === filterValue;
    });
  };

  const SelectColumnFilter = ({
    'column': {filterValue, setFilter},
  }) => {
    useEffect(() => {
      if (typeof filterValue === 'undefined') {
        setFilter('ACTIVO');
      }
    }, [filterValue, setFilter]);

    const options = [
      {'key': 'all', 'value': 'TODOS', 'text': 'TODOS'},
      {'key': 'active', 'value': 'ACTIVO', 'text': 'ACTIVO'},
      {'key': 'inactive', 'value': 'INACTIVO', 'text': 'INACTIVO'},
    ];

    return (
      <Select
        compact
        placeholder="ACTIVO"
        value={typeof filterValue !== 'undefined' ? filterValue : 'ACTIVO'}
        options={options}
        style={{'minWidth': '120px', 'width': '100%'}}
        onChange={(e, {value}) => {
          setFilter(value);
        }}
      />
    );
  };

  const columns = [
    {
      'Header': makeSortableHeader('NO.', 'id'),
      'accessor': 'id',
    },
    {
      'Header': makeSortableHeader('CONCESIONARIO', 'dealerName'),
      'accessor': 'dealerName',
      'Filter': ExcelColumnFilter,
      'filter': exactTextFilter,
    },
    {
      'Header': makeSortableHeader('AGENCIA', 'agency'),
      'accessor': 'agency',
      'Filter': ExcelColumnFilter,
      'filter': exactTextFilter,
    },
    {
      'Header': makeSortableHeader('CÉDULA', 'document'),
      'accessor': 'document',
      'Filter': ExcelColumnFilter,
      'filter': exactTextFilter,
    },
    {
      'Header': makeSortableHeader('NOMBRE', 'names'),
      'accessor': 'names',
      'Filter': ExcelColumnFilter,
      'filter': exactTextFilter,
    },
    {
      'Header': makeSortableHeader('APELLIDO', 'lastNames'),
      'accessor': 'lastNames',
      'Filter': ExcelColumnFilter,
      'filter': exactTextFilter,
    },
    {
      'Header': makeSortableHeader('CARGO', 'charge'),
      'accessor': 'charge',
      'Filter': ExcelColumnFilter,
      'filter': exactTextFilter,
    },
    {
      'Header': makeSortableHeader('FECHA INGRESO', 'startDate'),
      'accessor': 'startDate',
      'Cell': ({value}) => (value ? moment(value).format('DD/MM/YYYY') : ''),
    },
    {
      'Header': makeSortableHeader('ÚLTIMO CERTIFICADO', 'allCertificates'),
      'accessor': 'allCertificates',
      'Filter': ExcelColumnFilter,
      'filter': exactTextFilter,
      'Cell': ({value}) => (Array.isArray(value) && value.length > 0 ? value[0] : ''),
    },
    {
      'Header': makeSortableHeader('USUARIO', 'user'),
      'accessor': 'user',
      'Filter': ExcelColumnFilter,
      'filter': exactTextFilter,
      'Cell': (item) => (
        <div>
          <div>{item.value || 'Sin asignar'}</div>
          <small style={{'color': 'gray'}}> {item.row.original.gestor_email} </small>
        </div>
      ),
    },
    {
      'Header': makeSortableHeader('ESTADO', 'status'),
      'accessor': 'status',
      'Filter': SelectColumnFilter,
      'filter': (rows, id, filterValue) => {
        if (filterValue === 'TODOS' || typeof filterValue === 'undefined') {
          return rows;
        }
        const colId = Array.isArray(id) ? id[0] : id;
        return rows.filter((row) => row.values[colId] === filterValue);
      },
      'Cell': (item) => {
        return <Checkbox toggle checked={item.value === 'ACTIVO'} className='green'
          onChange={(e, input) => updateStatus(e, input.checked, item.row.original)} />;
      },
    },
    {
      'Header': () => <span className="header-title"
        style={{'userSelect': 'none', 'whiteSpace': 'nowrap'}}>ACCIONES</span>,
      'id': 'skills_actions',
      'width': 1,
      'disableFilters': true,
      'disableSortBy': true,
      'Cell': (item) => {
        return <div className='flex center end'>
          <WtsEmployeeModal mode='edit' employeeToEdit={item.row.original} />
        </div>;
      },
    },
  ];

  const clearFilters = () => {
    setTableKey((prev) => prev + 1);
  };

  return (
    <Container fluid>
      <Card raised fluid style={{'backgroundColor': 'transparent'}}>
        <div className='title-bar'>
          <Grid>
            <Grid.Row>
              <Grid.Column>
                <div className='title'>
                  <h2>Mantenimiento de Participantes</h2>
                </div>
              </Grid.Column>
            </Grid.Row>
            <Grid.Row textAlign='left'>
              <Grid.Column width={5}>
                <div className="ui fluid labeled input">
                  <div className="ui label label">Área de capacitación</div>
                  <Dropdown
                    fluid
                    selection
                    labeled
                    className='labeled-dropdown'
                    value={areaSelected?.id}
                    onChange={(e, i) => onChangeWtsAreaSelected(e, i)}
                    options={getDropdownOptions(areas, 'id', 'description')}
                  />
                </div>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </div>
      </Card>
      <Card raised fluid>
        {loading ? renderTableLoader() :
          <Grid padded>
            <Grid.Row style={{'padding': 0}}>
              <Grid.Column textAlign='left' width={14}>
                <div style={{'display': 'flex', 'alignItems': 'center', 'gap': '14px'}}>
                  <WtsEmployeeModal mode='new' nextId={nextEmployeeId} />
                  <div style={{
                    'display': 'inline-flex',
                    'alignItems': 'center',
                    'gap': '8px',
                    'backgroundColor': '#f7f7f7',
                    'border': '1px solid #e0e0e0',
                    'borderRadius': '5px',
                    'padding': '5px 14px',
                  }}>
                    <span style={{
                      'backgroundColor': '#21ba45',
                      'color': 'white',
                      'borderRadius': '5px',
                      'padding': '2px 8px',
                      'fontSize': '0.82em',
                      'fontWeight': '600',
                      'letterSpacing': '0.3px',
                    }}>
                      {filteredCount}
                    </span>
                    <span style={{
                      'color': '#555',
                      'fontSize': '0.85em',
                      'fontWeight': '500',
                    }}>
                      participantes
                    </span>
                  </div>
                  <Button
                    basic
                    color="blue"
                    size="small"
                    compact
                    onClick={clearFilters}
                  >
                    <Icon name="cancel" /> Limpiar Filtros
                  </Button>
                </div>
              </Grid.Column>
              <Grid.Column width={2} textAlign='right'>
                <WtsSkillsTechDetailsModal />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row>
              <Grid.Column floated='left' width={16}>
                <div className="table-sticky-actions">
                  <style>{`
                    .table-sticky-actions table thead th {
                      background-color: rgba(74, 144, 226, 0.5) !important;
                      color: #ffffff !important;
                    }
                    .table-sticky-actions table tbody tr:nth-child(odd) td {
                      background-color: #ffffff !important;
                    }
                    .table-sticky-actions table tbody tr:nth-child(even) td {
                      background-color: #e6f2ff !important;
                    }
                    .table-sticky-actions table th:last-child,
                    .table-sticky-actions table td:last-child {
                      position: sticky !important;
                      right: 0 !important;
                      z-index: 10 !important;
                      box-shadow: -3px 0px 5px -2px rgba(0,0,0,0.15) !important;
                    }
                    .table-sticky-actions table thead th:last-child {
                      background-color: rgba(74, 144, 226, 0.5) !important;
                      z-index: 11 !important;
                    }
                    .table-sticky-actions table tbody tr:nth-child(odd) td:last-child {
                      background-color: #ffffff !important;
                    }
                    .table-sticky-actions table tbody tr:nth-child(even) td:last-child {
                      background-color: #e6f2ff !important;
                    }
                  `}</style>
                  <CommercialTable
                    key={tableKey}
                    columns={columns}
                    data={sortedEmployees}
                    defaultColumnFilter={DefaultColumnFilter}
                    defaultPagination={10}
                    setFilteredData={(rows) => setFilteredCount(rows.length)}
                    initialState={{
                      'filters': [
                        {
                          'id': 'status',
                          'value': 'ACTIVO',
                        },
                      ],
                    }}
                  />
                </div>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        }
      </Card>
    </Container>
  );
};

export default WtsEmployees;
